import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import ExportOptions from "../../ExportOptions";
import TableSkeleton from "../../TableSkeleton";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const REPORT_URL = "https://api.muktihospital.com/api/nicu/discount-report";

const filterOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "daily", label: "Daily" },
  { value: "yearly", label: "Yearly" },
  { value: "all", label: "All Time" },
];

const NicuDiscountReport = () => {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({ perUser: [], grandTotal: 0, totalBills: 0 });
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const itemsPerPage = 10;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterType === "daily") { params.startDate = startDate; params.endDate = endDate; }
    else if (filterType === "monthly") { const [y, m] = selectedMonth.split("-"); params.year = y; params.month = m; }
    else if (filterType === "yearly") { params.year = selectedYear; }

    const cacheKey = `nicu_discount_report_${filterType}_${JSON.stringify(params)}`;

    try {
      const res = await axios.get(REPORT_URL, { headers: { "x-api-key": API_KEY }, params });
      setBills(res.data.bills || []);
      setSummary(res.data.summary || { perUser: [], grandTotal: 0, totalBills: 0 });

      // Cache this filter for offline viewing.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        bills: res.data.bills || [],
        summary: res.data.summary || { perUser: [], grandTotal: 0, totalBills: 0 },
      });
    } catch (err) {
      console.error("Error loading discount report:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.bills) {
          setBills(cached.bills);
          setSummary(cached.summary || { perUser: [], grandTotal: 0, totalBills: 0 });
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved discount report.");
        } else {
          setBills([]);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load discount report.");
        setBills([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return bills.filter((b) =>
      (b.discountBy || "").toLowerCase().includes(q) ||
      (b.patientName || "").toLowerCase().includes(q) ||
      (b.billNumber || "").toLowerCase().includes(q)
    );
  }, [bills, searchQuery]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

  const headers = ["Bill No", "Patient", "Discount", "Discount By", "Date"];
  const dataMapper = (b) => [b.billNumber, b.patientName, b.discount, b.discountBy, fmtDate(b.billingDate)].join("\t");
  const breadcrumbs = useMemo(() => [{ label: "Dashboard", url: "/" }, { label: "NICU" }, { label: "Discount Report" }], []);

  // color palette for per-user cards
  const cardColors = ["text-blue-600", "text-green-600", "text-purple-600", "text-orange-600", "text-pink-600", "text-teal-600"];

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden"><PageHeading title="NICU Discount Report" breadcrumbs={breadcrumbs} /></div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}

      {/* Summary: grand total + per-user */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 print-hidden">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Discount</p>
          <p className="text-xl font-black text-blue-600">৳{Math.round(summary.grandTotal)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-400">
          <p className="text-xs text-gray-500">Total Bills</p>
          <p className="text-xl font-black text-gray-700">{summary.totalBills}</p>
        </div>
        {summary.perUser.slice(0, 6).map((u, i) => (
          <div key={u.discountBy} className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 truncate">By {u.discountBy}</p>
            <p className={`text-xl font-black ${cardColors[i % cardColors.length]}`}>৳{Math.round(u.totalDiscount)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <h2 className="text-base font-medium text-gray-700">Discount Details</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-32">
              <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} placeholder="Filter" />
            </div>
            {filterType === "daily" && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
              </>
            )}
            {filterType === "monthly" && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm" />}
            {filterType === "yearly" && <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm w-24" min="2000" max="2100" />}
            <input type="text" placeholder="Search name/patient..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-48 w-full p-2 border rounded-md text-sm" />
            <ExportOptions data={currentData} dropdownOpen={dropdownOpen} toggleDropdown={() => setDropdownOpen(!dropdownOpen)} title="NICU Discount Report" headers={headers} dataMapper={dataMapper} />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <table className="min-w-[700px] w-full"><TableSkeleton rowCount={itemsPerPage} columns={[{ width: "100px" }, { width: "140px" }, { width: "90px" }, { width: "120px" }, { width: "100px" }]} /></table>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No discount records found.</div>
          ) : (
            <table className="min-w-[700px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Bill No</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Patient</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Discount (৳)</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Discount By</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((b, idx) => (
                  <tr key={`${b.billNumber}-${idx}`} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-500">{b.billNumber}</td>
                    <td className="p-3 text-sm text-gray-700">{b.patientName || "N/A"}</td>
                    <td className="p-3 text-sm text-right font-bold text-red-600">৳{b.discount}</td>
                    <td className="p-3 text-sm text-gray-700">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{b.discountBy || "N/A"}</span>
                    </td>
                    <td className="p-3 text-sm text-gray-500">{fmtDate(b.billingDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 flex justify-end gap-2 print-hidden">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowLeft /></button>
            <span className="p-2 text-sm">Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowRight /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NicuDiscountReport;
